import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";

// MUI Components
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Modal,
  Backdrop,
  Fade,
  Grid // Correctly imported
} from "@mui/material";

// Icons
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import CarRepairIcon from "@mui/icons-material/CarRepair";
import PersonIcon from "@mui/icons-material/Person"; // Icon for Driver
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";

// Firebase
import { collection, onSnapshot, query, orderBy, deleteDoc, doc, getDoc } from "firebase/firestore";
import db from "../../firebase";

// Project Components
import Navbar from "../../components/Navbar"; 

import AddEditVehicle from "./AddEditVehicle";
import ServiceCenter from "./ServiceCenter"; 
import Driver from "./Driver"; // Import the Driver Component

const Vehicle = () => {
  const navigate = useNavigate();
  
  // --- STATES ---
  // view mode: 'list', 'form' (vehicle), 'driver', 'service'
  const [view, setView] = useState("list"); 
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Edit & Details State
  const [formId, setFormId] = useState(""); 
  const [detailsId, setDetailsId] = useState(""); 
  const [viewData, setViewData] = useState(null); 

  
  
  // --- 2. FETCH DATA ---
  useEffect(() => {
    const q = query(collection(db, "VehicleData"), orderBy("createdAt", "desc"));
    
   const unsubscribe = onSnapshot(q, (snapshot) => {
  const vehicleData = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
  setVehicles(vehicleData);
  setLoading(false);
}, (error) => {
  console.error("Error fetching vehicles:", error);
  setLoading(false);
});

    return () => unsubscribe();
  }, []);

  // --- HANDLERS ---

  const handleEdit = (id) => {
    setFormId(id);   
    setView("form"); 
  };

  const handleAdd = () => {
    setFormId("");   
    setView("form");
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this vehicle permanently?")) {
      try {
        await deleteDoc(doc(db, "VehicleData", id));
      } catch (err) {
        console.error("Error deleting vehicle:", err);
        alert("Failed to delete vehicle.");
      }
    }
  };

  const handleView = async (id) => {
    setDetailsId(id);
    const docRef = doc(db, "VehicleData", id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
        setViewData(docSnap.data());
    }
  };

  const handleCloseView = () => {
      setDetailsId("");
      setViewData(null);
  }

  // --- FILTER ---
  const filteredVehicles = vehicles.filter((v) => 
    (v.vehicleId || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (v.makeModel || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (v.currentlyOperatedBy || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Navbar />
      <Container>
        {/* HEADER SECTION */}
        <Header>
          <div>
            <h1>Vehicle Master</h1>
            <p>Overview of all company vehicles</p>
          </div>
          
          <Navigate>
            {/* Show Back Button if NOT in List View */}
            {view !== "list" ? (
                <Btns onClick={() => setView("list")} style={{ backgroundColor: "#555" }}>
                    <ArrowBackIcon /> Back to List
                </Btns>
            ) : (
                /* Search Bar (Only visible in list view) */
                <Box bgcolor="#fff" borderRadius="5px" p={0.5} mr={2}>
                   <TextField 
                      size="small" 
                      placeholder="Search Vehicle..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      variant="standard"
                      InputProps={{
                          disableUnderline: true,
                          startAdornment: (
                              <InputAdornment position="start">
                                  <SearchIcon color="action" />
                              </InputAdornment>
                          )
                      }}
                      sx={{ px: 1, minWidth: "200px" }}
                   />
                </Box>
            )}

            {/* --- TOP RIGHT BUTTONS --- */}
            
            <Btns 
                onClick={handleAdd}
                className={view === "form" ? "active" : ""}
            >
              <AddIcon />
              Add Vehicle
            </Btns>

            {/* <Btns 
                onClick={() => setView("driver")}
                className={view === "driver" ? "active" : ""}
            >
              <PersonIcon />
              Driver Master
            </Btns> */}

            {/* <Btns 
                onClick={() => setView("service")}
                className={view === "service" ? "active" : ""}
            >
              <CarRepairIcon />
              Service Center
            </Btns> */}

          </Navigate>
        </Header>

        {/* MAIN CONTENT SWITCHER */}
        <Main>
          {view === "list" && (
              <>
                {loading ? (
                    <Box display="flex" justifyContent="center" width="100%" p={5}>
                        <CircularProgress />
                    </Box>
                ) : filteredVehicles.length === 0 ? (
                    <Box p={4} width="100%" textAlign="center" bgcolor="#fff" borderRadius={2} boxShadow={1}>
                        <DirectionsCarIcon sx={{ fontSize: 40, color: "#ccc", mb: 1 }} />
                        <Typography color="textSecondary">
                            {searchTerm ? "No vehicles match your search." : "No vehicles found in database."}
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer component={Paper} sx={{ boxShadow: "0px 1px 3px rgba(0,0,0,0.1)", borderRadius: "8px" }}>
                    <Table>
                        <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                        <TableRow>
                            <StyledHeaderCell>Vehicle ID</StyledHeaderCell>
                            <StyledHeaderCell>Fuel Type</StyledHeaderCell>
                            <StyledHeaderCell>Insurance Exp.</StyledHeaderCell>
                            <StyledHeaderCell align="center">Actions</StyledHeaderCell>
                        </TableRow>
                        </TableHead>
                        <TableBody>
                        {filteredVehicles.map((vehicle) => (
                            <TableRow key={vehicle.id} hover>
                            <TableCell>
                                <Typography variant="body2" fontWeight="bold">
                                    {vehicle.vehicleId || "N/A"}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Chip 
                                    label={vehicle.fuelType || "Unknown"} 
                                    size="small" 
                                    color={vehicle.isEV ? "success" : "default"} 
                                    variant={vehicle.isEV ? "filled" : "outlined"}
                                    sx={{ borderRadius: "4px", height: "24px", fontSize: "0.75rem" }}
                                />
                            </TableCell>
                            <TableCell>
                                {vehicle.nextInsuranceDate ? (
                                    <span style={{ color: new Date(vehicle.nextInsuranceDate) < new Date() ? "red" : "inherit" }}>
                                        {vehicle.nextInsuranceDate}
                                    </span>
                                ) : "-"}
                            </TableCell>
                            <TableCell align="center">
                                {/* VIEW BUTTON */}
                                <IconButton size="small" onClick={() => handleView(vehicle.id)}>
                                    <VisibilityIcon fontSize="small" />
                                </IconButton>
                                {/* EDIT BUTTON */}
                                <IconButton size="small" color="primary" onClick={() => handleEdit(vehicle.id)}>
                                    <EditIcon fontSize="small" />
                                </IconButton>
                                {/* DELETE BUTTON */}
                                <IconButton size="small" style={{color: "#F44336"}} onClick={() => handleDelete(vehicle.id)}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </TableCell>
                            </TableRow>
                        ))}
                        </TableBody>
                    </Table>
                    </TableContainer>
                )}
              </>
          )}

          {/* ADD / EDIT FORM VIEW */}
          {view === "form" && (
              <AddEditVehicle 
                formId={formId} 
                onBack={() => setView("list")} 
              />
          )}

          {/* DRIVER MASTER VIEW */}
          {view === "driver" && (
              <Driver 
                onBack={() => setView("list")} 
              />
          )}

          {/* SERVICE CENTER VIEW */}
          {view === "service" && (
              <ServiceCenter 
                onBack={() => setView("list")} 
              />
          )}

        </Main>

        {/* VIEW DETAILS MODAL */}
        <Modal
            open={!!detailsId}
            onClose={handleCloseView}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 500 }}
        >
            <Fade in={!!detailsId}>
                <Box sx={modalStyle}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" component="h2">Vehicle Details</Typography>
                        <IconButton onClick={handleCloseView}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    {viewData ? (
                        <Grid container spacing={2}>
                            <Grid item xs={12}><Typography><strong>ID:</strong> {viewData.vehicleId}</Typography></Grid>
                            <Grid item xs={12}><Typography><strong>Model:</strong> {viewData.makeModel}</Typography></Grid>
                            <Grid item xs={6}><Typography><strong>Fuel:</strong> {viewData.fuelType}</Typography></Grid>
                            <Grid item xs={6}><Typography><strong>Driver:</strong> {viewData.currentlyOperatedBy}</Typography></Grid>
                            <Grid item xs={12}><Typography><strong>Insurance:</strong> {viewData.insuranceCompany}</Typography></Grid>
                            <Grid item xs={12}><Typography><strong>Policy No:</strong> {viewData.policyNumber}</Typography></Grid>
                            <Grid item xs={12}><Typography><strong>Expiry:</strong> {viewData.nextInsuranceDate}</Typography></Grid>
                        </Grid>
                    ) : (
                        <CircularProgress />
                    )}
                </Box>
            </Fade>
        </Modal>

      </Container>
    </>
  );
};

// --- STYLES ---

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2
};

const Container = styled.div`
  position: relative;
  width: 100%;
  padding: 20px 25px;
  background-color: #f5f5f5;
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  h1 {
    color: #000;
    font-size: 1.8rem;
    margin-bottom: 5px;
  }
  p {
    color: #666666;
    font-size: 0.9rem;
  }
`;

const Navigate = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Btns = styled.button`
  outline: none;
  border: none;
  background-color: #1a1a1a;
  color: #fff;
  display: flex;
  align-items: center;
  padding: 8px 15px;
  border-radius: 5px;
  gap: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.2s;

  .MuiSvgIcon-root {
    font-size: 20px;
  }

  &:hover {
    background-color: #333;
  }
  
  &.active {
    background-color: #D32F2F;
  }
`;

const Main = styled.div`
  margin-top: 20px;
`;

const StyledHeaderCell = styled(TableCell)`
  && {
    font-weight: bold;
    color: #444;
    text-transform: uppercase;
    font-size: 0.75rem;
    padding: 12px 16px;
  }
`;

export default Vehicle;